import { memo, type KeyboardEvent as ReactKeyboardEvent, useEffect, useRef } from "react";
import { useEditorStore } from "@/application/stores/editorStore";
import type { RawKeyInput } from "@/domain/vim/model/keystroke";
import type { Mode } from "@/domain/vim/model/mode";
import "./Board.css";

interface LineProps {
  readonly lineNumber: number;
  readonly text: string;
  readonly isCurrentLine: boolean;
  readonly cursorCol: number | null;
}

/**
 * 行単位コンポーネント（ADR-014）。propsが変わった行だけが再描画されるよう、
 * 素朴な形（プリミティブのみのprops）で`memo`する。
 */
const Line = memo(function Line({ lineNumber, text, isCurrentLine, cursorCol }: LineProps) {
  const before = cursorCol !== null ? text.slice(0, cursorCol) : text;
  const atCursor = cursorCol !== null ? (text[cursorCol] ?? " ") : null;
  const after = cursorCol !== null ? text.slice(cursorCol + 1) : "";

  return (
    <div
      className={["board__line", isCurrentLine ? "board__line--current" : null]
        .filter(Boolean)
        .join(" ")}
    >
      <span className="board__linenr">{lineNumber}</span>
      <span className="board__text">
        {atCursor === null ? (
          text
        ) : (
          <>
            {before}
            <span className="board__cursor">{atCursor}</span>
            {after}
          </>
        )}
      </span>
    </div>
  );
});

function modeLabel(mode: Mode): string {
  switch (mode) {
    case "normal":
      return "NORMAL";
    case "insert":
      return "-- INSERT --";
    case "visual":
      return "-- VISUAL --";
    case "visual-line":
      return "-- VISUAL LINE --";
    case "visual-block":
      return "-- VISUAL BLOCK --";
    case "command-line":
      return "COMMAND";
  }
}

function toRawKeyInput(event: ReactKeyboardEvent<HTMLDivElement>): RawKeyInput {
  return {
    key: event.key,
    code: event.code,
    ctrl: event.ctrlKey,
    alt: event.altKey,
    meta: event.metaKey,
    isComposing: event.nativeEvent.isComposing,
  };
}

interface BoardProps {
  readonly onKeydown: (input: RawKeyInput) => void;
  /** `passed`判定の瞬間に常磐で一度だけ脈打たせる（180ms・ui-design.md §7.3）。 */
  readonly pulse?: boolean;
}

/**
 * 墨板（エディタ本体。ui-design.md §5.5）。明るい紙面の中で唯一暗い面。
 * 全キーをvimエンジンへ渡し、離脱は`Tab`のみ許す（screen-flow.md §8.2）。
 * カーソルは明滅させない。
 */
export function Board({ onKeydown, pulse = false }: BoardProps) {
  const lines = useEditorStore((state) => state.editorState?.buffer.lines);
  const cursor = useEditorStore((state) => state.editorState?.cursor);
  const mode = useEditorStore((state) => state.editorState?.mode);
  const usedKeystrokes = useEditorStore((state) => state.editorState?.usedKeystrokes);
  const commandLine = useEditorStore((state) => state.editorState?.commandLine);
  const statusMessage = useEditorStore((state) => state.editorState?.statusMessage);
  const boardRef = useRef<HTMLDivElement>(null);

  // 遷移直後の1打目が捨てられないよう、演習が変わるたびに墨板へ初期フォーカスを置く
  // （N-007。呼び出し側がDrillViewを`key`で演習ごとに再マウントするため、
  // マウント時に一度フォーカスすれば演習の切り替わりにも追従する）。
  useEffect(() => {
    boardRef.current?.focus();
  }, []);

  function handleKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key === "Tab") {
      return;
    }
    event.preventDefault();
    if (event.nativeEvent.isComposing || event.key === "Process") {
      return;
    }
    onKeydown(toRawKeyInput(event));
  }

  if (!lines || !cursor || mode === undefined) {
    return null;
  }

  const statusLeft = mode === "command-line" ? (commandLine ?? "") : modeLabel(mode);

  return (
    <div className="board-wrapper">
      <div
        ref={boardRef}
        className={["board", pulse ? "board--pulse" : null].filter(Boolean).join(" ")}
        role="application"
        lang="en"
        aria-label="墨板。全ての打鍵をvimとして受け取ります。Tabキーでこの外へ移動できます"
        // biome-ignore lint/a11y/noNoninteractiveTabindex: role="application"のカスタムウィジェットは、WAI-ARIAの要件どおりtabIndexで自らフォーカス可能にする必要がある（screen-flow.md §8.2）
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <div className="board__lines">
          {lines.map((text, index) => (
            <Line
              // biome-ignore lint/suspicious/noArrayIndexKey: 行は挿入・削除で並び替わらず、行番号自体が安定した識別子
              key={index}
              lineNumber={index + 1}
              text={text}
              isCurrentLine={index === cursor.line}
              cursorCol={index === cursor.line ? cursor.col : null}
            />
          ))}
        </div>
        <div
          className={["board__status", mode === "insert" ? "board__status--insert" : null]
            .filter(Boolean)
            .join(" ")}
        >
          <span className="board__status-mode">{statusLeft}</span>
          <span className="board__status-position">
            {cursor.line + 1}:{cursor.col + 1}
          </span>
          <span className="board__status-count">手数 {usedKeystrokes ?? 0}</span>
          {statusMessage && <span className="board__status-message">{statusMessage}</span>}
        </div>
      </div>
      {/* 画面上にもTabでの離脱を明示する（音声読み上げのaria-labelだけに頼らない・ui-design.md §9）。 */}
      <p className="board-wrapper__hint">Tabキーでこの外へ移動できます</p>
    </div>
  );
}
